
































































































import { useAlgoliaSearch } from '@algolia-search/composables/useAlgoliaSearch';
import useAlgoliaSearchStore from '@algolia-search/stores/algoliaSearch';
import type { SearchSessionParams } from '@nosto/types';
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  useContext,
  useMeta,
  useRoute,
  useRouter,
  watch
} from '@nuxtjs/composition-api';
import { onSSR } from '@vue-storefront/core';
import { useMediaQuery } from '@vueuse/core';
import VaimoTopHeaderMobile from 'organisms/header/mobile/VaimoTopHeaderMobile.vue';
import VaimoTopHeader from 'organisms/header/VaimoTopHeader.vue';
import { storeToRefs } from 'pinia';
import LazyHydrate from 'vue-lazy-hydration';
import type { Route } from 'vue-router';

import LoadWhenVisible from '~/components/utils/LoadWhenVisible.vue';
import {
  CategoryListingLayoutEnum,
  useCart,
  useConfig,
  useStore,
  useUiNotification,
  useUiState,
  useUser
} from '~/composables';
import AppFooter from '~/diptyqueTheme/components/AppFooter.vue';
import TailwindSafeClasses from '~/diptyqueTheme/components/atoms/SafeClasses.vue';
import VaimoLoader from '~/diptyqueTheme/components/atoms/VaimoLoader.vue';
import VaimoAccountSidebar from '~/diptyqueTheme/components/organisms/sidebar/VaimoAccountSidebar.vue';
import VaimoPromoBanner from '~/diptyqueTheme/components/organisms/VaimoPromoBanner.vue';
import {
  useGoogleTagManager,
  useHeaderHeights,
  useHeaderScroll,
  useIPData,
  usePreviewMode,
  useThemeUiState
} from '~/diptyqueTheme/composable';
import { useBooxi } from '~/diptyqueTheme/composable/useBooxi';
import type { BooxiConfigInterface } from '~/diptyqueTheme/composable/useBooxi/types';
import { useFontPreloads } from '~/diptyqueTheme/composable/useFontPreloads';
import { useGetRedirect } from '~/diptyqueTheme/composable/useGeoIpRedirect';
import {
  lsGet,
  lsRemove,
  lsSet
} from '~/diptyqueTheme/composable/useLocalStorage';
import { useAccountStore } from '~/diptyqueTheme/stores/account';
import { useErrorLayoutStatusStore } from '~/diptyqueTheme/stores/error';
import { useMegaMenuMobileStore } from '~/diptyqueTheme/stores/megaMenuMobile';
import { useNostoStore } from '~/diptyqueTheme/stores/nosto';
import { Logger } from '~/helpers/logger';
import { useCategoryStore } from '~/modules/catalog/category/stores/category';
import cartGetters from '~/modules/core/getters/cartGetters';
import { useCartStore } from '~/modules/core/stores/cart';

import { useNostoApi } from '../composable/nosto/useNostoApi';

export default defineComponent({
  name: 'DefaultLayout',

  components: {
    LazyHydrate,
    VaimoLoader,
    LoadWhenVisible,
    TailwindSafeClasses,
    VaimoPromoBanner,
    AppFooter,
    VaimoTopHeaderMobile,
    VaimoTopHeader,
    VaimoAccountSidebar,
    NostoSession: () => import('atoms/NostoSession.vue'),
    NostoScript: () => import('atoms/NostoScript.vue'),
    VaimoRedirectModal: () =>
      import('~/diptyqueTheme/components/organisms/VaimoRedirectModal.vue'),
    LoginModal: () =>
      import('~/modules/customer/components/LoginModal/LoginModal.vue'),
    VaimoStoreSwitcherModal: () =>
      import(
        '~/diptyqueTheme/components/organisms/VaimoStoreSwitcher/VaimoStoreSwitcherModal.vue'
      ),
    VaimoMobileMenu: () =>
      import('organisms/header/mobile/VaimoMobileMenu.vue'),
    Notification: () => import('~/diptyqueTheme/components/Notification.vue'),
    ProductAvailabilityModal: () =>
      import(
        'organisms/product/ProductAvailability/ProductAvailabilityModal.vue'
      ),
    Account: () =>
      import('~/diptyqueTheme/components/organisms/account/index.vue'),
    VaimoContentLayer: () => import('molecules/VaimoContentLayer.vue'),
    ContentfulPreviewPanel: () => import('organisms/ContentfulPreviewPanel.vue')
  },
  setup() {
    const PLP_ELIGIBLE_ROUTES = ['category', 'category-preview'];
    const PDP_ROUTES = ['product', 'product-preview'];
    const HEADER_TRANSPARENT_ROUTES = {
      desktop: [...PLP_ELIGIBLE_ROUTES],
      mobile: [...PLP_ELIGIBLE_ROUTES, ...PDP_ROUTES]
    };
    const { app } = useContext();
    const nostoStore = useNostoStore();
    const route = useRoute();
    const { isPreviewMode } = usePreviewMode();
    const { getRedirectData, redirectLoader, redirectToStore } = useGetRedirect(
      app.context
    );
    const router = useRouter();
    const routePath = computed(() => route.value.path);
    const { load: loadStoresConfig, config: configStore } = useConfig();
    const { load: loadCart, cart: cart } = useCart();
    const { load: loadUser, user, isAuthenticated, logout } = useUser();
    const { hasDefaultNotifications } = useUiNotification();
    const { getPageviewDetails, getOpenLoginDetails, EVENT_TIMEOUT } =
      useGoogleTagManager();
    const accountStore = useAccountStore();
    const errorLayoutStatusStore = useErrorLayoutStatusStore();
    const { setCartInLocalStorageByCode } = useStore();
    const categoryStore = useCategoryStore();
    const getProductsTotalQuantity = computed(() => {
      const items = cartGetters.getItems(cart.value) ?? [];
      const sampleCount = items.reduce((sum, item) => {
        const additionalData = JSON.parse(item.additional_data ?? '{}');
        return sum + (additionalData?.is_sample ? item.quantity : 0);
      }, 0);

      const totalItems = Number(cartGetters.getTotalItems(cart.value)) || 0;

      return Math.max(0, totalItems - sampleCount);
    });

    const { categoryType } = storeToRefs(useCategoryStore());
    const isPlpCategoryPage = computed(
      () => categoryType.value === CategoryListingLayoutEnum.PLP
    );

    const isLoading = computed(() => redirectLoader.value);
    const langClass = 'lang-' + app.i18n.localeProperties.code;
    const { booxiConfig } = useBooxi();
    const { booking: booxiData } = route.value.query;
    const cartStore = useCartStore();
    const isCartLoading = computed(() => cartStore.is_cart_loading);
    const addingItemsCount = computed(() => cartStore.adding_items_count);

    useFontPreloads();

    const loadCartFinished = ref(false);
    const { getCountryCodeByIP, userCountry } = useIPData();
    const checkCartStatus = () => {
      if (loadCartFinished.value) {
        loadCart({ realCart: true, forceReload: true });
        loadCartFinished.value = false;
      }
    };
    const { connectAlgoliaScript } = useAlgoliaSearch();
    const { setSearchModalOpen } = useAlgoliaSearchStore();

    const promoBannerRef = ref(null);
    const promoBannerDesktopRef = ref(null);

    const promoBannerHeight = computed(() => {
      return Number(promoBannerRef.value?.promoBannerHeight) || 0;
    });

    const promoBannerDesktopHeight = computed(() => {
      return Number(promoBannerDesktopRef.value?.promoBannerHeight) || 0;
    });

    watch(() => getProductsTotalQuantity.value, checkCartStatus);

    watch(
      () => isCartLoading.value,
      (newValue) => {
        if (!newValue) {
          loadCartFinished.value = true;
        }
      }
    );

    watch(
      () => addingItemsCount.value,
      (newValue) => {
        if (newValue === 0) {
          loadCartFinished.value = true;
          checkCartStatus();
        } else {
          loadCartFinished.value = false;
        }
      }
    );

    const {
      notifyMeProductId,
      isNotifyMeModalOpen,
      isCartSidebarOpen,
      isLoginModalOpen,
      toggleLoginModal,
      toggleCartSidebar
    } = useUiState();
    const { isStoreSwitcherModalOpen, closeStoreSwitcherModal } =
      useThemeUiState();
    const megaMenuMobileStore = useMegaMenuMobileStore();
    const { isOpen: isMobileMenuVisible } = storeToRefs(megaMenuMobileStore);
    const isDesktop = useMediaQuery('(min-width: 1024px)');

    const showMobileMenu = () => {
      megaMenuMobileStore.show();
    };

    const hideMobileMenu = () => {
      megaMenuMobileStore.hide();
      megaMenuMobileStore.hideMenuLayers();
    };

    const showHideMobileMenu = () => {
      if (isMobileMenuVisible.value) {
        hideMobileMenu();
        return;
      }

      showMobileMenu();
    };

    const triggerPageview = (to: Route) => {
      if (to?.name.includes('thank-you')) return;

      const statusCode =
        errorLayoutStatusStore.getErrorStatusCode()?.toString() ?? null;
      const pageviewDetails = getPageviewDetails(
        user.value,
        isAuthenticated.value,
        to,
        statusCode
      );

      if (pageviewDetails) {
        setTimeout(() => {
          app.$gtm.push(pageviewDetails);
        }, EVENT_TIMEOUT);
      }
    };

    onSSR(async () => {
      const storeConfig = configStore.value;

      if (!storeConfig || Object.keys(storeConfig).length === 0) {
        await loadStoresConfig();
      }
    });

    const apiState = app.context.$vsf.$magento.config.state;
    const changeStoreAndSetCart = async () => {
      //avoid loading cart once again in case of redirect from booxi and from klarna
      if (booxiData || isShopperRedirect.value) return;
      // Remove countries && allowed countries from LS when store changed
      lsRemove('allowed_countries');
      lsRemove('all_countries');

      if (!isCartLoading.value) {
        await loadCart();
      }

      const currentCartId = apiState.getCartId();

      setCartInLocalStorageByCode(currentCartId);

      const storeCart = lsGet('store_cart');
      const storeCartObj = storeCart ? storeCart : { user: {}, guest: {} };
      const userType = isAuthenticated.value ? 'user' : 'guest';
      const userTypeStoreCart = storeCartObj ? storeCartObj[userType] : {};
      const cartId = userTypeStoreCart
        ? userTypeStoreCart[configStore.value.store_code]
        : currentCartId;

      if (cartId) {
        app.$vsf.$magento.config.state.setCartId(cartId);
      } else {
        app.$vsf.$magento.config.state.removeCartId();
      }
    };

    const handleSearchStart = () => {
      setSearchModalOpen(true);
      showHideMobileMenu();
    };

    const handleSearchClose = () => {
      setSearchModalOpen(false);
      showHideMobileMenu();
    };

    const handleCartToggle = () => {
      router.push(app.localeRoute({ name: 'cart' }));
      hideMobileMenu();
      sendCartGTMEvent();
    };

    const sendCartGTMEvent = () => {
      app.$gtm.push({
        event: 'click_mini_cart_popin'
      });
    };

    const TOKEN_EXPIRED_LOCAL_KEY = 'vsf-user-token-expired';
    const expiredTokenHandler = async () => {
      if (!isAuthenticated.value) return;

      await logout({});
    };
    const expiredTokenLsHandler = async ({ key, newValue, oldValue }) => {
      if (key !== TOKEN_EXPIRED_LOCAL_KEY) return;

      try {
        if (
          !newValue ||
          (!JSON.parse(oldValue)?._value && JSON.parse(newValue)._value)
        ) {
          await expiredTokenHandler();
        }
      } catch (error) {
        Logger.error(['[default.vue] expiredTokenLsHandler', error]);
      }
    };

    onMounted(async () => {
      app.$bus.$on('token-expired', expiredTokenHandler);
      window.addEventListener('storage', expiredTokenLsHandler);

      await getCountryCodeByIP();
      if (configStore.value?.vaimo_geoip_general_enable) {
        const redirectData = await getRedirectData();

        if (redirectData.shouldBeRedirected) {
          redirectToStore(redirectData.path);
        }
      }
      await Promise.all([
        loadUser({
          customQuery: {
            customer: 'customerWithOptions'
          }
        }),
        changeStoreAndSetCart()
      ]);

      triggerPageview(route.value);

      localStorage.setItem(
        'magento_config',
        JSON.stringify(configStore.value || {})
      );

      window?.nostojs(async (api) => {
        try {
          const { segments }: SearchSessionParams =
            await api.getSearchSessionParams();
          if (segments.length) {
            nostoStore.setUserSegments(segments);
            Logger.debug(['[NOSTO] Updated customer segments', segments]);
          }
        } catch (e) {
          Logger.error(['[NOSTO] Updating customer segments failed', e]);
        }
      });
    });

    let unregisterAfterEach: (() => void) | undefined;

    const exceptionRoutes: string[] = ['checkout', 'thank-you'];
    unregisterAfterEach = router.afterEach((to: Route, from: Route) => {
      if (
        exceptionRoutes.some(
          (route) => to.name.includes(route) || from.name.includes(route)
        )
      )
        return;

      triggerPageview(to);
    });

    const isCheckout = computed(() => {
      const checkout = routePath.value.includes('checkout');
      const success = routePath.value.includes('checkout/thank-you');

      return checkout && !success;
    });

    // Header Scroll & Heights
    const { topOffset: headerTopOffset, threshold: headerThreshold } =
      useHeaderHeights(isDesktop, promoBannerHeight);
    const { scrollingClass } = useHeaderScroll(
      isMobileMenuVisible,
      isCheckout,
      hasDefaultNotifications,
      {
        topOffset: headerTopOffset.value,
        threshold: headerThreshold.value,
        scrollThrottle: 30,
        releaseScrollThreshold: 15
      }
    );

    const isHeaderTransparent = computed(() => {
      const routeName = route.value.name?.split('___')[0] || '';
      const deviceType = process.client
        ? isDesktop.value
          ? 'desktop'
          : 'mobile'
        : app.$device.isDesktop
        ? 'desktop'
        : 'mobile';

      return shouldBeTransparent(routeName, deviceType);
    });

    provide('is-header-transparent', isHeaderTransparent);

    const isShopperRedirect = computed(() =>
      routePath.value.includes('adyen/handleShopperRedirect')
    );

    const isExternalCart = computed(() =>
      routePath.value.includes('external-cart')
    );

    const checkoutClass = computed(() =>
      isCheckout.value ? 'is-checkout' : ''
    );

    function shouldBeTransparent(
      routeName: string,
      deviceType: 'desktop' | 'mobile'
    ) {
      if (!HEADER_TRANSPARENT_ROUTES[deviceType]) return false;

      const isTransparentRoute =
        HEADER_TRANSPARENT_ROUTES[deviceType].includes(routeName);
      if (!isTransparentRoute) return false;

      // extra rule for category-type pages, transparent only for the PLP
      if (PLP_ELIGIBLE_ROUTES.includes(routeName)) {
        return categoryStore?.categoryType === 'plp';
      }

      return true;
    }

    useNostoApi(async (api) => {
      const locale = app.i18n.localeProperties.code;
      const NOSTO_CUSTOMER_KEY = ['nostoSession', locale].join('_');
      api.setAutoLoad(false);
      api.visit.setCustomerIdentifierService({
        getCustomerId: () => {
          const cookieKey = apiState.getNostoSession();
          const localKey = lsGet(NOSTO_CUSTOMER_KEY) as string;

          return localKey === cookieKey ? cookieKey : localKey;
        },

        setCustomerId: (id) => {
          apiState.setNostoSession(id, {
            path: `/${locale}`
          });
          lsSet(NOSTO_CUSTOMER_KEY, id);
        }
      });
    }, 'CONFIG');

    onBeforeUnmount(() => {
      app.$bus.$off('token-expired', expiredTokenHandler);
      window.removeEventListener('storage', expiredTokenLsHandler);
      unregisterAfterEach?.();
    });

    const metaTags = computed(() => {
      return (
        isPreviewMode.value && {
          meta: [
            {
              hid: 'robots',
              name: 'robots',
              content: 'noindex nofollow'
            }
          ]
        }
      );
    });

    useMeta(() => metaTags.value);

    connectAlgoliaScript();

    const toggleAccountSidebar = () => {
      if (isAuthenticated.value) {
        accountStore.isVisible = true;
      } else {
        toggleCartSidebar('account');
      }
    };

    const getClickAccountDetails = () => {
      app.$gtm.push(
        getOpenLoginDetails(route.value.fullPath, isAuthenticated.value)
      );
    };

    const handleAccountToggle = () => {
      toggleAccountSidebar();
      hideMobileMenu();
      getClickAccountDetails();
    };

    // This check is necessary to avoid recreating the InstantSearchSsr component when the URL is updated.
    const searchPageKey = computed(() => {
      return route.value.name && route.value.name.includes('algolia-search')
        ? route.value.path
        : route.value.fullPath;
    });

    return {
      isPlpCategoryPage,
      isLoading,
      isCartSidebarOpen,
      isLoginModalOpen,
      toggleLoginModal,
      showHideMobileMenu,
      handleCartToggle,
      handleAccountToggle,
      handleSearchStart,
      handleSearchClose,
      getProductsTotalQuantity,
      sendCartGTMEvent,
      isMobileMenuVisible,
      route,
      scrollingClass,
      notifyMeProductId,
      isNotifyMeModalOpen,
      isStoreSwitcherModalOpen,
      closeStoreSwitcherModal,
      isDesktop,
      toggleCartSidebar,
      isCheckout,
      isExternalCart,
      accountStore,
      errorLayoutStatusStore,
      checkoutClass,
      configStore,
      booxiConfig,
      langClass,
      userCountry,
      cart,
      user,
      isPreviewMode,
      searchPageKey,
      isHeaderTransparent,
      promoBannerRef,
      promoBannerDesktopRef,
      promoBannerHeight,
      promoBannerDesktopHeight
    };
  },
  head() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const klarnaClientId = this?.configStore?.klarna_pay_instalments_client_id;
    const scriptArray = [];

    // Strongly type booxiConfig
    const booxi = (this as any)?.booxiConfig as
      | BooxiConfigInterface
      | undefined;
    if (booxi?.is_booxi_enabled && booxi?.booxi_script_url) {
      scriptArray.push({
        src: booxi.booxi_script_url,
        hid: 'booxi',
        defer: true
      });
    }

    if (klarnaClientId) {
      scriptArray.push({
        src: 'https://osm.klarnaservices.com/lib.js',
        async: true,
        'data-client-id': klarnaClientId
      });
    }

    return scriptArray.length > 0 ? { script: scriptArray } : {};
  }
});
